import React, { useEffect, useState } from "react";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import PageHeader from "../../components/ui/PageHeader";
import Textarea from "../../components/ui/Textarea";
import { getSettingsRequest, updateSettingsRequest } from "../../services/api/settings.api";

interface FormState {
    siteName: string;
    siteEmail: string;
    sitePhone: string;
    siteAddress: string;
    headCode: string;
    bodyCode: string;
    purchaseTaxPercent: string;
    ticketDeliveryPrice: string;
    stripeCommission: string;
    invoiceText: string;

    // Годовой абонемент клуба
    subscriptionPrice: string;
    subscriptionPercent: string;
    subscriptionTitleRu: string;
    subscriptionTitleEn: string;
    subscriptionTitleDe: string;
    subscriptionTitleUa: string;
    subscriptionDescriptionRu: string;
    subscriptionDescriptionEn: string;
    subscriptionDescriptionDe: string;
    subscriptionDescriptionUa: string;
}

const initialFormState: FormState = {
    siteName: "",
    siteEmail: "",
    sitePhone: "",
    siteAddress: "",
    headCode: "",
    bodyCode: "",
    purchaseTaxPercent: "",
    ticketDeliveryPrice: "",
    stripeCommission: "",
    invoiceText: "",

    subscriptionPrice: "",
    subscriptionPercent: "",
    subscriptionTitleRu: "",
    subscriptionTitleEn: "",
    subscriptionTitleDe: "",
    subscriptionTitleUa: "",
    subscriptionDescriptionRu: "",
    subscriptionDescriptionEn: "",
    subscriptionDescriptionDe: "",
    subscriptionDescriptionUa: "",
};

const SettingsPage: React.FC = () => {
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const loadSettings = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getSettingsRequest();

            setFormState({
                siteName: response.site_name ?? "",
                siteEmail: response.site_email ?? "",
                sitePhone: response.site_phone ?? "",
                siteAddress: response.site_address ?? "",
                headCode: response.html_head ?? "",
                bodyCode: response.html_body ?? "",
                purchaseTaxPercent: response.purchase_tax_percent ?? "0",
                ticketDeliveryPrice: response.ticket_delivery_price ?? "0",
                stripeCommission: response.stripe_commission ?? "0",
                invoiceText: response.invoice_text ?? "",

                subscriptionPrice: response.subscription_price ?? "15",
                subscriptionPercent: response.subscription_percent ?? "10",
                subscriptionTitleRu: response.subscription_title_ru ?? "",
                subscriptionTitleEn: response.subscription_title_en ?? "",
                subscriptionTitleDe: response.subscription_title_de ?? "",
                subscriptionTitleUa: response.subscription_title_ua ?? "",
                subscriptionDescriptionRu: response.subscription_description_ru ?? "",
                subscriptionDescriptionEn: response.subscription_description_en ?? "",
                subscriptionDescriptionDe: response.subscription_description_de ?? "",
                subscriptionDescriptionUa: response.subscription_description_ua ?? "",
            });
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить настройки");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadSettings();
    }, []);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setPageError(null);
        setSuccessMessage(null);
        setIsSaving(true);

        try {
            await updateSettingsRequest({
                site_name: formState.siteName.trim() || null,
                site_email: formState.siteEmail.trim() || null,
                site_phone: formState.sitePhone.trim() || null,
                site_address: formState.siteAddress.trim() || null,
                html_head: formState.headCode || null,
                html_body: formState.bodyCode || null,
                purchase_tax_percent: formState.purchaseTaxPercent || null,
                ticket_delivery_price: formState.ticketDeliveryPrice || null,
                stripe_commission: formState.stripeCommission || null,
                invoice_text: formState.invoiceText || null,

                subscription_price: formState.subscriptionPrice || null,
                subscription_percent: formState.subscriptionPercent || null,
                subscription_title_ru: formState.subscriptionTitleRu || null,
                subscription_title_en: formState.subscriptionTitleEn || null,
                subscription_title_de: formState.subscriptionTitleDe || null,
                subscription_title_ua: formState.subscriptionTitleUa.trim() || null,
                subscription_description_ru: formState.subscriptionDescriptionRu || null,
                subscription_description_en: formState.subscriptionDescriptionEn || null,
                subscription_description_de: formState.subscriptionDescriptionDe || null,
                subscription_description_ua: formState.subscriptionDescriptionUa.trim() || null,
            });

            setSuccessMessage("Настройки успешно сохранены.");
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось сохранить настройки");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Настройки"
                description="Базовые настройки сайта и системные HTML-вставки для head и body."
            />

            <Card className="content-form-card">
                {pageError ? <div className="form-global-error">{pageError}</div> : null}
                {successMessage ? <div className="form-global-success">{successMessage}</div> : null}

                {isLoading ? (
                    <div className="table-skeleton">
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                        <div className="table-skeleton-row" />
                    </div>
                ) : (
                    <form className="editor-form" onSubmit={handleSubmit}>
                        <div className="form-grid form-grid-two-columns">
                            <Field label="Название сайта" htmlFor="settings-site-name">
                                <Input
                                    id="settings-site-name"
                                    value={formState.siteName}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, siteName: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="E-mail сайта" htmlFor="settings-site-email">
                                <Input
                                    id="settings-site-email"
                                    type="email"
                                    value={formState.siteEmail}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, siteEmail: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Телефон" htmlFor="settings-site-phone">
                                <Input
                                    id="settings-site-phone"
                                    value={formState.sitePhone}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, sitePhone: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Адрес" htmlFor="settings-site-address">
                                <Input
                                    id="settings-site-address"
                                    value={formState.siteAddress}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, siteAddress: event.target.value }))
                                    }
                                />
                            </Field>
                        </div>

                        <Field label="HEAD code" htmlFor="settings-head-code">
                            <Textarea
                                id="settings-head-code"
                                rows={8}
                                value={formState.headCode}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, headCode: event.target.value }))
                                }
                            />
                        </Field>

                        <Field label="BODY code" htmlFor="settings-body-code">
                            <Textarea
                                id="settings-body-code"
                                rows={8}
                                value={formState.bodyCode}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, bodyCode: event.target.value }))
                                }
                            />
                        </Field>
                        <div className="form-grid form-grid-two-columns">
                            <Field label="Процент налога" htmlFor="settings-site-tax">
                                <Input
                                    id="settings-site-tax"
                                    value={formState.purchaseTaxPercent}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, purchaseTaxPercent: event.target.value }))
                                    }
                                />
                            </Field>

                            <Field label="Cтоимость доставки билета" htmlFor="settings-site-delivery">
                                <Input
                                    id="settings-site-delivery"
                                    value={formState.ticketDeliveryPrice}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, ticketDeliveryPrice: event.target.value }))
                                    }
                                />
                            </Field>
                        </div>
                        <div className="form-grid form-grid-two-columns">
                            <Field label="Комиссия Stripe %" htmlFor="settings-site-stripe">
                                <Input
                                    id="settings-site-stripe"
                                    value={formState.stripeCommission}
                                    onChange={(event) =>
                                        setFormState((prev) => ({ ...prev, stripeCommission: event.target.value }))
                                    }
                                />
                            </Field>
                        </div>
                        <Field label="Текст для счёта" htmlFor="settings-body-invoice">
                            <Textarea
                                id="settings-body-invoice"
                                rows={8}
                                value={formState.invoiceText}
                                onChange={(event) =>
                                    setFormState((prev) => ({ ...prev, invoiceText: event.target.value }))
                                }
                            />
                        </Field>

                        {/* Годовой абонемент клуба */}
                        <h3 className="card-title" style={{ marginTop: "24px" }}>
                            Абонемент клуба
                        </h3>
                        <p className="card-text">
                            Цена и процент подставляются по умолчанию на сайте и в панели.
                            Описание показывается на странице покупки абонемента.
                        </p>

                        <div className="form-grid form-grid-two-columns">
                            <Field label="Цена абонемента, €" htmlFor="settings-subscription-price">
                                <Input
                                    id="settings-subscription-price"
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={formState.subscriptionPrice}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            subscriptionPrice: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field
                                label="Процент скидки на билеты"
                                htmlFor="settings-subscription-percent"
                            >
                                <Input
                                    id="settings-subscription-percent"
                                    type="number"
                                    min={0}
                                    max={100}
                                    step="0.01"
                                    value={formState.subscriptionPercent}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            subscriptionPercent: event.target.value,
                                        }))
                                    }
                                />
                            </Field>
                        </div>

                        <div className="form-grid form-grid-two-columns">
                            <Field label="Заголовок RU" htmlFor="settings-subscription-title-ru">
                                <Input
                                    id="settings-subscription-title-ru"
                                    value={formState.subscriptionTitleRu}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            subscriptionTitleRu: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок DE" htmlFor="settings-subscription-title-de">
                                <Input
                                    id="settings-subscription-title-de"
                                    value={formState.subscriptionTitleDe}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            subscriptionTitleDe: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок UA" htmlFor="settings-subscription-title-ua">
                                <Input
                                    id="settings-subscription-title-ua"
                                    value={formState.subscriptionTitleUa}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            subscriptionTitleUa: event.target.value,
                                        }))
                                    }
                                />
                            </Field>

                            <Field label="Заголовок EN" htmlFor="settings-subscription-title-en">
                                <Input
                                    id="settings-subscription-title-en"
                                    value={formState.subscriptionTitleEn}
                                    onChange={(event) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            subscriptionTitleEn: event.target.value,
                                        }))
                                    }
                                />
                            </Field>
                        </div>

                        <Field
                            label="Описание страницы RU"
                            htmlFor="settings-subscription-description-ru"
                        >
                            <Textarea
                                id="settings-subscription-description-ru"
                                rows={6}
                                value={formState.subscriptionDescriptionRu}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        subscriptionDescriptionRu: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field
                            label="Описание страницы DE"
                            htmlFor="settings-subscription-description-de"
                        >
                            <Textarea
                                id="settings-subscription-description-de"
                                rows={6}
                                value={formState.subscriptionDescriptionDe}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        subscriptionDescriptionDe: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field
                            label="Описание страницы UA"
                            htmlFor="settings-subscription-description-ua"
                        >
                            <Textarea
                                id="settings-subscription-description-ua"
                                rows={6}
                                value={formState.subscriptionDescriptionUa}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        subscriptionDescriptionUa: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <Field
                            label="Описание страницы EN"
                            htmlFor="settings-subscription-description-en"
                        >
                            <Textarea
                                id="settings-subscription-description-en"
                                rows={6}
                                value={formState.subscriptionDescriptionEn}
                                onChange={(event) =>
                                    setFormState((prev) => ({
                                        ...prev,
                                        subscriptionDescriptionEn: event.target.value,
                                    }))
                                }
                            />
                        </Field>

                        <div className="editor-actions">
                            <Button type="submit" disabled={isSaving}>
                                {isSaving ? "Сохраняем..." : "Сохранить настройки"}
                            </Button>
                        </div>
                    </form>
                )}
            </Card>
        </div>
    );
};

export default SettingsPage;
